import { Link } from "react-router";
import {
  ArrowRight,
  CloudOff,
  Compass,
  FileCheck2,
  Ruler,
  ShieldCheck,
  Timer,
  Webhook,
} from "lucide-react";
import { NFR_TARGETS, SEVERITY_META, SEVERITY_TIERS } from "@taskiq/shared";
import { SeverityChip } from "@/components/ui/Badge";
import { Button, NudgeArrow } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { IconList } from "@/components/ui/IconList";
import { IconTile } from "@/components/ui/IconTile";
import { AssessmentReplay } from "@/components/marketing/AssessmentReplay";
import { Reveal } from "@/components/marketing/Reveal";
import { CtaBand, PageHero, Section, SectionIntro } from "@/components/marketing/Section";

/**
 * The home page.
 *
 * Its job is one belief: that a real pre-task hazard assessment fits inside the
 * time a technician actually has. Everything above the fold argues that, and the
 * argument is made by showing the ninety seconds rather than by claiming them -
 * see `AssessmentReplay`.
 *
 * The copy is drawn from the PRD's own positioning (§15.1) and requirements
 * triage (§4) rather than invented, and the "what we don't claim" section is
 * deliberate: §18 lists over-promising on measurement as a top-severity risk, and
 * saying no in public is what makes the yeses credible.
 */

const CAPABILITIES = [
  {
    icon: Compass,
    title: "Area scan",
    body: "A guided 20–30 second sweep returns a specific, plain-language hazard list. Wasp nest in the eave. Loose rock along the path. Overhead line above the ladder position.",
    span: true,
  },
  {
    icon: Ruler,
    title: "Working at heights",
    body: `Two facts, and they are not the same kind of fact. The phone flat against the rail reads the 4:1 setup angle off its own inclinometer, to about ${NFR_TARGETS.ladderAngleToleranceDeg}°. Then it photographs the set-up, and a model reads the feet, the tie-off, the rails and the extension above the landing off the picture. Ground firmness it refuses to guess at, and says so.`,
    span: true,
  },
  {
    icon: ShieldCheck,
    title: "PPE check",
    body: "Front camera, before the climb. Hard hat, glasses, gloves, hi-vis, boots, harness and lanyard read from the photograph, each one found, not there, or not in shot - and every one of them the technician's to overrule. Equipment detection only, never facial recognition.",
  },
  {
    icon: CloudOff,
    title: "Works with no signal",
    body: "Capture never needs one, and neither does the angle. What a model reads is asked for when there is coverage and comes back as “not in shot” when there is not - never as an answer nobody looked for. The queue syncs when the signal does, and the technician always knows which state they are in.",
  },
  {
    icon: Webhook,
    title: "Fits your systems",
    body: "Standalone by default. Webhooks, a REST API, scheduled exports and a BI feed sit on top as an optional layer - connect one system, several, or none.",
  },
];

const LIMITS = [
  {
    title: "Ground firmness",
    body: "No camera measures soil bearing capacity. The detector is never even asked about it - it arrives as a plain switch, after the rows a model answered, labelled as the technician's own word.",
  },
  {
    title: "Wiring inside walls",
    body: "Nothing optical sees through drywall. We flag the visible outlets and panels near a drill point and prompt you to think about the runs between them - an advisory, not a detector.",
  },
  {
    title: "Ladder length",
    body: "A single phone lens cannot measure absolute distance reliably. Depth-sensor phones can estimate it, and that will only ever be guidance - never a measurement of record.",
  },
];

export function Home() {
  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <PageHero
        size="lg"
        eyebrow="Pre-task hazard assessment"
        title="The last ninety seconds before the job starts."
        lead={
          <>
            Every other safety tool tells you what went wrong. TaskIQ tells you what is in front of
            you - before you start. No cameras to install, no network changes. Just the phone
            already in the technician&rsquo;s pocket.
          </>
        }
        aside={<AssessmentReplay />}
      >
        <div className="hero__actions">
          <Link to="/signup">
            <Button variant="panel" size="lg" trailingIcon={<NudgeArrow />}>
              Start free
            </Button>
          </Link>
          <Link to="/product">
            <Button variant="panel-ghost" size="lg">
              See how it works
            </Button>
          </Link>
        </div>

        <IconList
          tone="panel-accent"
          className="hero__claims"
          items={[
            { icon: Timer, text: "Under 90 seconds" },
            { icon: CloudOff, text: "Offline-compatible" },
            { icon: FileCheck2, text: "Audit-ready record" },
          ]}
        />
      </PageHero>

      {/* ── The problem ───────────────────────────────────────────────────── */}
      <Section>
        <div className="split">
          <SectionIntro
            size="sm"
            eyebrow="Why it exists"
            title="A technician working against the clock thinks about finishing the task, not about how to start it safely."
            lead="Tunnel vision is the norm, not a lapse. The wasp nest, the loose rock and the service drop overhead all sit outside the cone of attention - not because nobody looked, but because looking is unpaid time on a route with eleven more stops on it."
          >
            <p className="prose prose--lg company-copy__p">
              TaskIQ takes ninety seconds to widen it, and never blocks the work. It warns, records
              and escalates.
            </p>
          </SectionIntro>

          <Reveal delay={80}>
            <Card padding="lg">
              <p className="eyebrow eyebrow--md eyebrow--subtle">What it replaces</p>
              <dl className="def-list">
                <div key={"A paper tailboard form"} className="def-list__item def-list__item--sm">
                  <dt className="blurb__title">{"A paper tailboard form"}</dt>
                  <dd className="blurb__body">
                    {
                      "Filled in at the truck, often after the fact, filed in a binder nobody opens until an incident."
                    }
                  </dd>
                </div>
                <div key={"A checkbox app"} className="def-list__item def-list__item--sm">
                  <dt className="blurb__title">{"A checkbox app"}</dt>
                  <dd className="blurb__body">
                    {
                      "Faster to complete than paper and just as blind - it records that a box was ticked, not what was in front of the crew."
                    }
                  </dd>
                </div>
                <div key={"Fixed-camera analytics"} className="def-list__item def-list__item--sm">
                  <dt className="blurb__title">{"Fixed-camera analytics"}</dt>
                  <dd className="blurb__body">
                    {
                      "Excellent on a site you own. Useless at the eleventh service address of the day."
                    }
                  </dd>
                </div>
              </dl>
            </Card>
          </Reveal>
        </div>
      </Section>

      {/* ── Capabilities ──────────────────────────────────────────────────── */}
      <Section tone="sunken">
        <SectionIntro
          eyebrow="What it does"
          title="Four checks and a record, on the job"
          lead="In the order a technician would do them anyway, on the phone already in their pocket. Nothing here needs a tripod, a second person or anything installed at the site."
        />

        {/* Two wide tiles then three narrow ones: the scan and the heights check
            are what carry the product, and giving all five the same box would
            say they matter equally. They do not - and heights needs the room
            twice over, because it is the one capability here that is two
            different kinds of check under one name. */}
        <ul className="capability-grid">
          {CAPABILITIES.map(({ icon: Icon, title, body, span }, index) => (
            <Reveal
              as="li"
              key={title}
              delay={index * 60}
              className={span ? "capability-grid__item--wide" : "capability-grid__item"}
            >
              <Card padding="md" fill>
                <IconTile size="lg" className="capability__tile">
                  <Icon />
                </IconTile>
                <div>
                  <h3 className="blurb__title blurb__title--lg">{title}</h3>
                  <p className="blurb__body blurb__body--lg">{body}</p>
                </div>
              </Card>
            </Reveal>
          ))}
        </ul>
      </Section>

      {/* ── The vocabulary ────────────────────────────────────────────────── */}
      <Section>
        <div className="split split--wide-right">
          <SectionIntro
            eyebrow="The vocabulary"
            title="Three words, and everyone means the same thing by them"
            lead="A finding is something you note, slow down for, or refuse to work under. That is the whole scale - because a hazard list with nine levels is a hazard list that gets skimmed."
          />

          <Reveal delay={80}>
            <ul className="severity-list">
              {SEVERITY_TIERS.map((tier) => (
                <Card as="li" key={tier} padding="sm" className="severity-list__item">
                  <div className="severity-list__chip">
                    <SeverityChip severity={tier} />
                  </div>
                  <p className="severity-list__body">{SEVERITY_META[tier].description}</p>
                </Card>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      {/* ── The record ────────────────────────────────────────────────────── */}
      <Section tone="sunken">
        <SectionIntro
          eyebrow="For the office"
          title="Every assessment becomes a record you can defend"
          lead="The crew gets ninety seconds back. The people answering to a regulator get something the paper form never gave them: what was actually in front of the technician, and what they did about it."
        />

        <ul className="record-grid">
          {[
            {
              t: "Timestamped and geotagged",
              d: "Written on the device at the moment of capture, not when the phone next found a tower.",
            },
            {
              t: "Findings and review actions",
              d: "Each hazard, its tier, and whether the technician acknowledged, noted or escalated it.",
            },
            {
              t: "Scoped to who may see it",
              d: "A crew lead sees their sites, an EHS director sees the company. Enforced in the database, not the interface.",
            },
            {
              t: "Exportable",
              d: "Scheduled exports, a REST API and webhooks into the compliance system you already run.",
            },
          ].map(({ t, d }, index) => (
            <Reveal as="li" key={t} delay={index * 60}>
              <Card padding="sm" fill>
                <div>
                  <h3 className="blurb__title blurb__title--lg">{t}</h3>
                  <p className="blurb__body blurb__body--lg">{d}</p>
                </div>
              </Card>
            </Reveal>
          ))}
        </ul>
      </Section>
      <CtaBand />
    </>
  );
}
